// revBerriRoute.js

/**
 * Distances cumulées approximatives réelles depuis Gouin / Berri.
 *
 * Important :
 * - Ce fichier ne définit PAS les intersections.
 * - Les intersections restent dans Velo.jsx.
 * - Ici, on associe seulement certaines intersections connues à une distance réelle.
 *
 * Calibration confirmée :
 * Gouin -> Sauriol ≈ 0,75 km.
 */
export const REV_BERRI_CUMULATIVE_KM_BY_INTERSECTION_ID = {
  Gouin: 0,

  // Point de calibration important.
  Sauriol: 0.75,

  // Ces valeurs viennent de l'ancien modèle.
  // Elles pourront être raffinées plus tard.
  Commune: 12.7,
  "Canal Lachine": 15.1,
  "Parc René-Lévesque": 28.05,
  "Parc René-Lévesque2": 28.2
}

export const REV_BERRI_TOTAL_KM = 28.2

export const REV_BERRI_ROUTE = {
  id: "rev-berri",
  name: "REV Berri",
  subtitle: "Réseau Express de Vélo",
  startLabel: "Gouin / Berri",
  originalEndLabel: "de la Commune / Berri",
  finalEndLabel: "Parc René-Lévesque",
  routeName: "Gouin-Berri vers parc René-Lévesque",
  displayRoute: "Gouin → de la Commune → canal Lachine → parc René-Lévesque",
  ogTitle: "REV Berri — le jeu de vélo d'Ahuncycle",
  ogDescription:
    "Pars de Gouin et Berri, descends jusqu'à de la Commune, puis poursuis le canal Lachine jusqu'au parc René-Lévesque.",
  ogImage: "/images/og/rev-berri.jpg",
  publicPath: "/jeux/velo",
  totalKm: REV_BERRI_TOTAL_KM
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function toFiniteNumber(value, fallback = null) {
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

/**
 * Construit les points de route à partir des intersections déjà définies
 * dans Velo.jsx.
 *
 * On ajoute Gouin manuellement seulement si l'intersection n'existe pas déjà.
 */
export function buildRevBerriRoutePoints(intersections = []) {
  const safeIntersections = Array.isArray(intersections) ? intersections : []

  const hasGouin = safeIntersections.some((point) => point.id === "Gouin")

  const normalizedIntersections = hasGouin
    ? safeIntersections
    : [
        {
          offsetX: 0,
          id: "Gouin",
          z: 0,
          width: 17,
          direction: 0
        },
        ...safeIntersections
      ]

  return normalizedIntersections
    .filter((point) => Number.isFinite(point.z))
    .map((point) => ({
      ...point,
      label: point.id === "Gouin" ? "Gouin / Berri" : point.id,
      cumulativeKm:
        Object.prototype.hasOwnProperty.call(
          REV_BERRI_CUMULATIVE_KM_BY_INTERSECTION_ID,
          point.id
        )
          ? REV_BERRI_CUMULATIVE_KM_BY_INTERSECTION_ID[point.id]
          : null
    }))
}

/**
 * Garde seulement les points qui ont une distance réelle connue.
 */
export function buildRevBerriRouteAnchors(intersections = []) {
  return buildRevBerriRoutePoints(intersections)
    .filter(
      (point) =>
        Number.isFinite(point.z) &&
        Number.isFinite(point.cumulativeKm)
    )
    .sort((a, b) => b.z - a.z)
}

export function getRevBerriTotalKm(intersections = []) {
  const anchors = buildRevBerriRouteAnchors(intersections)
  return anchors[anchors.length - 1]?.cumulativeKm || 0
}

/**
 * Calcule la distance ABSOLUE depuis Gouin selon un z.
 *
 * Exemple :
 * currentZ = -1678
 * => environ 0,75 km, si Sauriol est à z: -1678.
 */
export function calculateAbsoluteRouteKmFromZ({
  currentZ,
  intersections = []
}) {
  const z = toFiniteNumber(currentZ)

  const empty = {
    absoluteKm: 0,
    completionPercent: 0,
    completedRoute: false,
    from: null,
    to: null,
    currentSegment: null
  }

  if (z === null) return empty

  const anchors = buildRevBerriRouteAnchors(intersections)

  if (anchors.length < 2) return empty

  const first = anchors[0]
  const last = anchors[anchors.length - 1]
  const totalKm = last.cumulativeKm

  if (z >= first.z) {
    return {
      absoluteKm: first.cumulativeKm,
      completionPercent: 0,
      completedRoute: false,
      from: first,
      to: anchors[1] || null,
      currentSegment: {
        from: first.id,
        to: anchors[1]?.id || null,
        localRatio: 0,
        segmentKm: anchors[1]
          ? anchors[1].cumulativeKm - first.cumulativeKm
          : 0
      }
    }
  }

  if (z <= last.z) {
    const beforeLast = anchors[anchors.length - 2] || null

    return {
      absoluteKm: last.cumulativeKm,
      completionPercent: 100,
      completedRoute: true,
      from: beforeLast,
      to: last,
      currentSegment: {
        from: beforeLast?.id || null,
        to: last.id,
        localRatio: 1,
        segmentKm: beforeLast
          ? last.cumulativeKm - beforeLast.cumulativeKm
          : 0
      }
    }
  }

  for (let i = 0; i < anchors.length - 1; i += 1) {
    const from = anchors[i]
    const to = anchors[i + 1]

    const isInsideSegment = z <= from.z && z >= to.z

    if (!isInsideSegment) continue

    const segmentZLength = Math.abs(to.z - from.z)
    const playerZIntoSegment = Math.abs(z - from.z)

    const localRatio =
      segmentZLength === 0
        ? 0
        : clamp(playerZIntoSegment / segmentZLength, 0, 1)

    const segmentKm = to.cumulativeKm - from.cumulativeKm
    const absoluteKm = from.cumulativeKm + localRatio * segmentKm

    return {
      absoluteKm,
      completionPercent:
        totalKm > 0 ? clamp((absoluteKm / totalKm) * 100, 0, 100) : 0,
      completedRoute: absoluteKm >= totalKm,
      from,
      to,
      currentSegment: {
        from: from.id,
        to: to.id,
        localRatio,
        segmentKm
      }
    }
  }

  return empty
}

/**
 * Calcule la distance de la PARTIE ACTUELLE.
 *
 * Important pour les téléportations :
 * - currentZ = position actuelle.
 * - runStartZ = z du niveau où la partie a commencé.
 *
 * Si tu démarres à Gouin :
 * runStartZ = 0.
 *
 * Si tu te téléportes à Cherrier :
 * runStartZ = -10400.
 *
 * La distance affichée repart donc à 0 au niveau téléporté.
 */
export function calculateRouteProgressKmFromZ({
  currentZ,
  runStartZ = 0,
  intersections = [],
  traveledBeforeCurrentRunKm = 0
}) {
  const anchors = buildRevBerriRouteAnchors(intersections)
  const lastAnchor = anchors[anchors.length - 1]
  const absoluteTotalKm = lastAnchor?.cumulativeKm || 0

  const current = calculateAbsoluteRouteKmFromZ({
    currentZ,
    intersections
  })

  const start = calculateAbsoluteRouteKmFromZ({
    currentZ: runStartZ,
    intersections
  })

  const currentRunDistanceKm = Math.max(
    0,
    current.absoluteKm - start.absoluteKm
  )

  const distanceKm =
    Math.max(0, Number(traveledBeforeCurrentRunKm) || 0) +
    currentRunDistanceKm

  return {
    // Distance réellement parcourue dans la partie.
    // Ne saute pas à 12,72 km lors d'un téléport à Commune.
    distanceKm,

    // Distance parcourue depuis le dernier départ/téléport.
    currentRunDistanceKm,

    // Distance absolue sur le trajet complet.
    // Utile pour le pourcentage global.
    absoluteKm: current.absoluteKm,

    // Distance absolue du point où le run courant a commencé.
    startAbsoluteKm: start.absoluteKm,

    runStartZ,
    traveledBeforeCurrentRunKm,

    // Pourcentage global du parcours complet.
    // Exemple à Commune : environ 45 %, même si distanceKm reste la vraie distance jouée.
    completionPercent:
      absoluteTotalKm > 0
        ? clamp((current.absoluteKm / absoluteTotalKm) * 100, 0, 100)
        : 0,

    completedRoute: current.absoluteKm >= absoluteTotalKm,
    from: current.from,
    to: current.to,
    currentSegment: current.currentSegment
  }
}

/**
 * Compatibilité avec l'ancien appel.
 *
 * Si Velo.jsx n'a pas encore currentZ, mais seulement currentGameDistance,
 * on suppose temporairement :
 *
 * currentZ = -Math.abs(currentGameDistance)
 *
 * Mais l'idéal reste de passer currentZ directement.
 */
export function calculateRouteProgressKm({
  currentGameDistance,
  currentZ,
  runStartZ = 0,
  intersections = []
}) {
  const resolvedCurrentZ = Number.isFinite(currentZ)
    ? currentZ
    : -Math.abs(Number(currentGameDistance) || 0)

  return calculateRouteProgressKmFromZ({
    currentZ: resolvedCurrentZ,
    runStartZ,
    intersections
  })
}

export function getRoutePointById(id, intersections = []) {
  return buildRevBerriRoutePoints(intersections).find((point) => point.id === id) || null
}

export function getRouteStartZById(id, intersections = [], fallbackZ = 0) {
  const point = getRoutePointById(id, intersections)
  return Number.isFinite(point?.z) ? point.z : fallbackZ
}

export function getNearestRoutePointFromZ(currentZ, intersections = []) {
  const z = toFiniteNumber(currentZ)

  if (z === null) return null

  return buildRevBerriRoutePoints(intersections).reduce((nearest, point) => {
    if (!Number.isFinite(point.z)) return nearest

    if (!nearest) return point

    const currentDistance = Math.abs(point.z - z)
    const nearestDistance = Math.abs(nearest.z - z)

    return currentDistance < nearestDistance ? point : nearest
  }, null)
}

export function formatKm(value) {
  return `${(Number(value) || 0).toFixed(2).replace(".", ",")} km`
}

export function formatPercent(value) {
  return `${Math.round(Number(value) || 0)} %`
}